/**
 * cv-proposal-draft — the LOCAL, per-user record of which CV-proposal entries the person took
 * OUT, shared by the two surfaces that curate the same proposal (`career-cv-workbench` and the
 * standalone `career-cv-proposal` route).
 *
 * This one is not about losing typed text. The proposal is seeded ALL-SELECTED on every read,
 * by design — the model's reading is the starting point and the person's job is to remove what
 * is wrong, not to retype their CV. So an interrupted session does not come back as an empty
 * form that obviously needs redoing; it comes back looking finished, with every rejected entry
 * silently ticked again. The next »übernehmen« then writes those entries into the durable CV.
 * The failure is a WRONG DURABLE WRITE, which is why this ranks above the text rescues.
 *
 * Two consequences for the shape:
 *
 *   1. It persists the REJECTIONS, never the selections. The default is "everything", so
 *      recording the subtractions is what survives a proposal that gained entries — a newly
 *      proposed entry the person has never seen correctly defaults to selected instead of being
 *      silently dropped by a stale selection list.
 *   2. It is scoped to the proposal's identity (`proposedAt`). A re-ingest produces a new
 *      proposal, and decisions about the old one must not be replayed onto it.
 *
 * The storage plumbing and the untrusted-boundary discipline live in `local-draft.ts`; this
 * module owns the shape, its structural guard, and the two pure functions the surfaces share so
 * the rule cannot drift between them.
 */

import { createLocalDraftStore, type LocalDraftStore } from "./local-draft"

/** The persisted decisions: which proposal, and what was taken out of it. */
export interface CvProposalRejectionDraft {
  /** The `proposedAt` of the proposal these rejections belong to. */
  proposedAt: string
  /** The entry keys the person unticked. */
  rejected: string[]
  /** When this draft was last touched (ISO-8601). */
  updatedAt: string
}

/** The minimum an entry must expose for the selection rules — both surfaces' rows satisfy it. */
export interface SelectableEntry {
  key: string
  /** A required entry (the name) can never be unticked; the CV cannot exist without it. */
  required?: boolean
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null

/** A total, dependency-free guard for a persisted rejection set. */
export function isCvProposalRejectionDraft(v: unknown): v is CvProposalRejectionDraft {
  if (!isRecord(v)) return false
  return (
    typeof v["proposedAt"] === "string" &&
    v["proposedAt"].length > 0 &&
    Array.isArray(v["rejected"]) &&
    v["rejected"].every((k) => typeof k === "string") &&
    typeof v["updatedAt"] === "string"
  )
}

export type CvProposalRejectionStore = LocalDraftStore<CvProposalRejectionDraft>

const KEY_PREFIX = "career:cv-proposal:rejections:v1:"

/**
 * A per-user rejection store. Deliberately ONE slot across both surfaces: they curate the same
 * `/api/cv/proposal` resource, so a decision made in the workbench must be honoured by the
 * standalone route and the other way round.
 */
export function createCvProposalRejectionStore(userId: string | null, explicitStorage?: Storage): CvProposalRejectionStore {
  return createLocalDraftStore(KEY_PREFIX, userId, isCvProposalRejectionDraft, explicitStorage)
}

/**
 * The all-selected default, minus any rejections rescued for THIS proposal.
 *
 * A draft from a different (or unknown) proposal is ignored rather than partially applied — the
 * entry keys of a re-ingest are not comparable, and a half-honoured decision set is worse than
 * an honest fresh start. Required entries stay selected whatever the draft claims.
 */
export function selectionFor(
  entries: readonly SelectableEntry[],
  proposedAt: string | null,
  draft: CvProposalRejectionDraft | null,
): ReadonlySet<string> {
  const all = entries.map((e) => e.key)
  if (!draft || proposedAt === null || draft.proposedAt !== proposedAt) return new Set(all)
  const rejected = new Set(draft.rejected)
  return new Set(all.filter((key) => !rejected.has(key) || entries.find((e) => e.key === key)?.required === true))
}

/** The entry keys currently taken out — what {@link selectionFor} reads back. */
export function rejectionsOf(entries: readonly SelectableEntry[], selected: ReadonlySet<string>): string[] {
  return entries.filter((e) => !selected.has(e.key)).map((e) => e.key)
}
